import React, { useCallback, useEffect, useState } from 'react';
import { INGEST_URL, LEADS_TEST_MODE, formatDateTime, timeAgo, rpcError } from './config';
import { L } from './ui';

const EXAMPLE = `POST ${INGEST_URL}
X-EnMarcha-Key: emk_…  (o Authorization: Bearer emk_…)
Content-Type: application/json

{
  "crm_lead_id": "123456",            // obligatorio: id del lead en el CRM
  "customer_name": "Nombre Apellidos", // obligatorio
  "phone": "600 000 000",
  "email": "cliente@ejemplo.com",
  "city": "Fuenlabrada",
  "customer_message": "Mensaje del cliente",
  "vehicle_text": "Seat Ibiza 1.0 TSI 2021",
  "vehicle_plate": "1234BBB",
  "vehicle_url": "https://www.flexicar.es/…",
  "vehicle_price": 12990,             // al contado
  "vehicle_price_financed": 11990,
  "crm_url": "https://…/lead/123456",
  "received_at": "2026-10-27T10:15:00+01:00",
  "notes": "Canal, agente, etc.",
  "source": "crm_connector"           // crm_api | extension | crm_connector
}`;

const STATUS = {
  creado: { label: 'Creado', color: 'var(--t-86efac)' },
  duplicado: { label: 'Ya existía', color: 'var(--t-9ca3af)' },
  error: { label: 'Error', color: 'var(--t-f87171)' }
};

// Claves de conexión para que el CRM (o un conector/extensión) mande los leads online solos.
// Solo creador y responsable. La clave completa se enseña una única vez; en la base de datos solo queda su hash.
export default function CrmConnection({ supabase }) {
  const [keys, setKeys] = useState([]);
  const [log, setLog] = useState([]);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [testMode, setTestMode] = useState(LEADS_TEST_MODE);
  const [monitor, setMonitor] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    const [k, g] = await Promise.all([
      supabase.from('lead_ingest_keys').select('id, name, key_hint, active, test_mode, monitor, last_heartbeat_at, heartbeat_status, heartbeat_detail, connection_alerted_at, created_at, revoked_at, last_used_at').order('created_at', { ascending: false }),
      supabase.from('lead_ingest_log').select('id, key_id, crm_lead_id, status, lead_id, error, created_at').order('created_at', { ascending: false }).limit(30)
    ]);
    if (k.error || g.error) { setError(rpcError(k.error || g.error)); return; }
    setKeys(k.data || []); setLog(g.data || []);
  }, [supabase]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setNewKey(null);
    const { data, error: err } = await supabase.rpc('create_lead_ingest_key', { p_name: name, p_test_mode: testMode, p_monitor: monitor });
    setBusy(false);
    if (err) { setError(rpcError(err)); return; }
    setNewKey(data); setName('');
    load();
  };

  const revoke = async (k) => {
    if (!window.confirm(`¿Revocar la clave "${k.name}"? Lo que la use dejará de poder enviar leads.`)) return;
    setError('');
    const { error: err } = await supabase.rpc('revoke_lead_ingest_key', { p_id: k.id });
    if (err) { setError(rpcError(err)); return; }
    if (newKey && newKey.id === k.id) setNewKey(null);
    load();
  };

  const toggleMonitor = async (k) => {
    setError('');
    const { error: err } = await supabase.rpc('set_lead_ingest_key_monitor', { p_id: k.id, p_monitor: !k.monitor });
    if (err) { setError(rpcError(err)); return; }
    load();
  };

  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); setCopied(what); setTimeout(() => setCopied(''), 2000); } catch (e) { /* sin portapapeles */ }
  };

  const keyName = (id) => keys.find(k => k.id === id)?.name || '—';
  const active = keys.filter(k => k.active);
  const revoked = keys.filter(k => !k.active);

  return (
    <div style={{ display: 'grid', gap: '12px', minWidth: 0 }}>
      <div style={L.warn}>
        Los leads que envía el CRM entran como <strong>🌐 Online</strong>, sin asignar, y avisan igual que uno registrado a mano.
        Si llega dos veces el mismo id del CRM, no se duplica. <strong>Usar datos reales de clientes requiere autorización de Flexicar.</strong>
      </div>
      {error && <div style={L.error}>{error}</div>}

      <div style={L.section}>
        <h4 style={L.sectionTitle}>Punto de entrada</h4>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ color: 'var(--t-e2e8f0)', fontSize: '0.8rem', wordBreak: 'break-all', flex: '1 1 260px' }}>{INGEST_URL}</code>
          <button type="button" style={{ ...L.secondary, padding: '5px 10px', fontSize: '0.8rem' }} onClick={() => copy(INGEST_URL, 'url')}>
            {copied === 'url' ? '✅ Copiado' : '📋 Copiar'}
          </button>
        </div>
        <details style={{ marginTop: '10px' }}>
          <summary style={{ color: 'var(--t-cbd5e1)', cursor: 'pointer', fontSize: '0.85rem' }}>Formato de los datos (para quien monte la conexión)</summary>
          <pre style={{ backgroundColor: 'var(--b-111827)', color: 'var(--t-e2e8f0)', padding: '10px', borderRadius: '6px', fontSize: '0.75rem', overflowX: 'auto', whiteSpace: 'pre', marginTop: '8px' }}>{EXAMPLE}</pre>
          <p style={{ ...L.muted, margin: '6px 0 0' }}>
            Se puede mandar un lead, una lista (máx. 50) o {'{ "leads": [ … ] }'}. Respuesta: un resultado por lead (creado, ya existía o error).
            Límite: 600 leads por clave y hora.
          </p>
        </details>
      </div>

      <div style={L.section}>
        <h4 style={L.sectionTitle}>Claves de conexión</h4>
        {newKey && (
          <div style={{ ...L.ok, display: 'grid', gap: '8px' }}>
            <strong>Clave creada. Cópiala ahora: no se volverá a mostrar.</strong>
            <code style={{ wordBreak: 'break-all', fontSize: '0.8rem' }}>{newKey.key}</code>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" style={{ ...L.secondary, padding: '5px 10px', fontSize: '0.8rem' }} onClick={() => copy(newKey.key, 'key')}>
                {copied === 'key' ? '✅ Copiada' : '📋 Copiar clave'}
              </button>
              <button type="button" style={{ ...L.secondary, padding: '5px 10px', fontSize: '0.8rem' }} onClick={() => setNewKey(null)}>Ya la he guardado</button>
            </div>
            <span style={{ fontSize: '0.78rem' }}>Guárdala solo donde la necesite el conector. No la mandes por correo ni chat; si se filtra, revócala y crea otra.</span>
          </div>
        )}
        <form onSubmit={create} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
          <input aria-label="Nombre de la clave" placeholder='Nombre (p. ej. "Conector CRM tienda")' value={name} maxLength={60}
            onChange={e => setName(e.target.value)} style={{ ...L.input, marginBottom: 0, flex: '1 1 220px' }} />
          {LEADS_TEST_MODE && (
            <label style={{ ...L.muted, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={testMode} onChange={e => setTestMode(e.target.checked)} />
              Leads de prueba 🧪
            </label>
          )}
          <label style={{ ...L.muted, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
            title="Para la extensión del ordenador de la tienda: avisa por correo si deja de dar señal">
            <input type="checkbox" checked={monitor} onChange={e => setMonitor(e.target.checked)} />
            Avisar si se pierde la conexión
          </label>
          <button type="submit" style={L.button} disabled={busy || !name.trim()}>{busy ? 'Creando…' : '🔑 Crear clave'}</button>
        </form>
        {active.length === 0 && <p style={{ ...L.muted, margin: 0 }}>No hay claves activas: el CRM no puede enviar leads todavía.</p>}
        <div style={{ display: 'grid', gap: '8px' }}>
          {active.map(k => (
            <div key={k.id} style={{ ...L.card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px' }}>
              <strong style={{ color: 'var(--t-f1f5f9)' }}>{k.name}</strong>
              <code style={L.muted}>emk_…{k.key_hint}</code>
              {k.test_mode && <span style={{ color: 'var(--t-fde68a)', fontSize: '0.75rem' }}>🧪 crea leads de prueba</span>}
              <span style={L.muted}>{k.last_used_at ? `Último lead ${formatDateTime(k.last_used_at)}` : 'Sin leads todavía'}</span>
              {k.monitor && <ConnectionState k={k} />}
              <button type="button" style={{ ...L.secondary, padding: '5px 10px', fontSize: '0.8rem' }} onClick={() => toggleMonitor(k)}
                title={k.monitor ? 'Dejar de avisar si se pierde la conexión' : 'Avisar por correo si deja de dar señal'}>
                {k.monitor ? '🔕 No vigilar' : '🔔 Vigilar conexión'}
              </button>
              <button type="button" style={{ ...L.danger, padding: '5px 10px', fontSize: '0.8rem', marginLeft: 'auto' }} onClick={() => revoke(k)}>Revocar</button>
            </div>
          ))}
        </div>
        {revoked.length > 0 && (
          <p style={{ ...L.muted, margin: '10px 0 0' }}>Revocadas: {revoked.map(k => `${k.name} (…${k.key_hint})`).join(', ')}</p>
        )}
      </div>

      <div style={L.section}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <h4 style={{ ...L.sectionTitle, margin: 0 }}>Últimas entradas</h4>
          <button type="button" style={{ ...L.secondary, padding: '4px 10px', fontSize: '0.8rem', marginLeft: 'auto' }} onClick={load}>↻ Actualizar</button>
        </div>
        {log.length === 0 ? <p style={{ ...L.muted, margin: 0 }}>Todavía no ha llegado nada desde el CRM.</p> : (
          <div style={{ display: 'grid', gap: '4px' }}>
            {log.map(r => {
              const st = STATUS[r.status] || { label: r.status, color: 'var(--t-9ca3af)' };
              return (
                <div key={r.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', fontSize: '0.8rem', color: 'var(--t-cbd5e1)', borderBottom: '1px solid var(--l-334155)', padding: '4px 0' }}>
                  <span style={{ color: 'var(--t-9ca3af)' }}>{formatDateTime(r.created_at)}</span>
                  <strong style={{ color: st.color }}>{st.label}</strong>
                  <span>CRM {r.crm_lead_id || '—'}</span>
                  <span style={{ color: 'var(--t-9ca3af)' }}>{keyName(r.key_id)}</span>
                  {r.error && <span style={{ color: 'var(--t-fca5a5)', flexBasis: '100%' }}>{r.error}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Estado de la señal "sigo conectado" de la extensión (se considera perdida a los 10 min sin señal).
function ConnectionState({ k }) {
  const age = k.last_heartbeat_at ? Date.now() - new Date(k.last_heartbeat_at).getTime() : null;
  let st;
  if (age == null) st = { icon: '⚪', text: 'Aún no ha conectado', color: 'var(--t-9ca3af)' };
  else if (age > 10 * 60000) st = { icon: '🔴', text: `Sin señal desde ${formatDateTime(k.last_heartbeat_at)}`, color: 'var(--t-f87171)' };
  else if (k.heartbeat_status === 'sesion_cerrada') st = { icon: '🟠', text: 'Sesión del CRM cerrada', color: 'var(--t-fbbf24)' };
  else if (k.heartbeat_status === 'error') st = { icon: '🟠', text: `Error al leer el CRM${k.heartbeat_detail ? `: ${k.heartbeat_detail}` : ''}`, color: 'var(--t-fbbf24)' };
  else st = { icon: '🟢', text: `Conectado (señal ${timeAgo(k.last_heartbeat_at)})`, color: 'var(--t-86efac)' };
  return <span style={{ color: st.color, fontSize: '0.8rem', fontWeight: 600 }}>{st.icon} {st.text}</span>;
}
